import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990vsForm990EZ = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Difference between Form 990 vs Form 990-EZ | TaxZerone "
          description="Understand key differences between Form 990 and 990-EZ to file correctly and stay IRS compliant. E-file easily with TaxZerone for your nonprofit today."
          keywords=""
          canonical="resources/tax-exempt/form-990-vs-form-990-ez/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          {' '}
          <span className="highlight-container">
            <span className="highlight">Form 990 </span>
          </span>{' '}
          vs{' '}
          <span className="highlight-container">
            <span className="highlight"> Form 990-EZ </span>
          </span>{' '}
          : Differences for
          <br /> Nonprofit Tax Filing
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Discover the differences in 990 forms which suit your organization’s
          need—with TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990vs990EZ`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-vs-form-990-ez/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 vs. Form 990-EZ at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Files Form 990?
                        </strong>{' '}
                        Organizations with gross receipts of{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $200,000 or more
                        </strong>{' '}
                        or total assets of{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $500,000 or more
                        </strong>{' '}
                        generally must{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          file Form 990.
                        </Link>{' '}
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Files Form 990-EZ?
                        </strong>{' '}
                        Organizations with gross receipts under{' '}
                        <strong style={{ fontWeight: 600 }}>$200,000</strong>{' '}
                        and total assets under{' '}
                        <strong style={{ fontWeight: 600 }}>$500,000</strong>{' '}
                        generally qualify to{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-ez/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          file Form 990-EZ.
                        </Link>{' '}
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Key Difference:
                        </strong>{' '}
                        Form 990 requires more comprehensive reporting on
                        finances, governance, and operations, while Form 990-EZ
                        is a simplified return designed for eligible smaller
                        organizations.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Filing Deadline:
                        </strong>{' '}
                        Both forms are generally due by the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          15th day of the 5th month
                        </strong>{' '}
                        after the end of the organization's tax year and may be
                        extended by filing{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 8868.
                        </Link>{' '}
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                {/* <Typography variant="body1" sx={{ mb: 2 }}>
                  Nonprofit organizations are required to{' '}
                  <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                    file 990 forms
                  </Link>{' '}
                  for an annual return with the IRS to maintain their tax-exempt
                  status.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Understanding the differences between them and knowing which
                  one applies to your organization is essential for staying
                  compliant. Here’s a simple breakdown to help you determine
                  which form you should file.
                </Typography> */}
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 990?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#formm990ez" sx={{ color: '#0202ff' }}>
                          What is Form 990-EZ?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Key Differences for Form 990 vs Form 990-EZ
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Scenarios
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Form 990?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }} component="ul">
                  <li style={{ marginBottom: 8 }}>
                    <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990
                    </Link>{' '}
                    is a detailed annual tax return that most tax-exempt
                    organizations must file with the IRS. This form is designed
                    for larger organizations that have annual gross receipts of
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      $200,000 or more
                    </span>{' '}
                    or total assets of{' '}
                    <span style={{ fontWeight: 600 }}>$500,000 or more</span>.
                  </li>
                  <li>
                    It provides a comprehensive, detailed report on finances,
                    programs, governance, and compensation, enhancing
                    transparency and accountability to IRS and the public.
                  </li>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="formm990ez"
                >
                  What is Form 990-EZ?
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-EZ
                    </Link>{' '}
                    is a shorter version of Form 990, intended for smaller
                    tax-exempt organizations with gross receipts of{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      less than $200,000
                    </span>{' '}
                    and total assets of{' '}
                    <span style={{ fontWeight: 600 }}>less than $500,000</span>.
                    It requires less detailed information, making it simpler and
                    quicker to complete.
                  </li>
                  <li>
                    It offers a simplified, less detailed report suitable for
                    smaller organizations, reducing the reporting burden while
                    maintaining transparency.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="key"
                >
                  Key Differences for Form 990 vs Form 990-EZ:
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Let&apos;s see the differences between form 990 vs Form 990-EZ
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Aspects
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Form 990
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Form 990-EZ
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing requirements
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Gross receipts  ≥ $200,000, or <br />
                            Total assets  ≥ $500,000
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            Gross receipts &lt; $200,000, and <br />
                            Total assets &lt; $500,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Parts required to file
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Required detailed information in{' '}
                            <span style={{ fontWeight: 600 }}>12</span> parts
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            Required less information in{' '}
                            <span style={{ fontWeight: 600 }}> 6</span> parts.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Schedules
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Form 990 has{' '}
                            <span style={{ fontWeight: 600 }}>
                              16 schedules{' '}
                            </span>
                            <ul style={{ fontWeight: 600, textAlign: 'left' }}>
                              <li>Schedule (A-O) </li>
                              <li>Schedule R</li>
                            </ul>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            Form 990- EZ has{' '}
                            <span style={{ fontWeight: 600 }}>8 schedules</span>
                            <ul style={{ fontWeight: 600, textAlign: 'left' }}>
                              <li>Schedule (A, B, C, E, G, L, N, O)</li>
                            </ul>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing options
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Can be filed electronically and allows extensions.
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            Can be filed electronically and allows six-month
                            extensions via{' '}
                            <Link
                              href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Form 8868
                            </Link>
                            .
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Penalties
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $25/day</span>,
                            max <span style={{ fontWeight: 600 }}>$12,500</span>{' '}
                            or <span style={{ fontWeight: 600 }}>5%</span> of
                            gross receipts.
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> $25/day</span>,
                            max <span style={{ fontWeight: 600 }}>$12,500</span>{' '}
                            or <span style={{ fontWeight: 600 }}>5%</span> of
                            gross receipts.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong>{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=990vs990ez`}
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-N
                  </Link>{' '}
                  has no penalty but loss of exempt status if not filed for 3
                  years
                </Alert>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 4,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                      fontWeight: 600,
                      mb: 0.5,
                      textAlign: 'left',
                    }}
                  >
                    Due Date{' '}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0, textAlign: 'left' }}>
                    Both <span style={{ fontWeight: 600 }}> Form 990</span> and{' '}
                    <span style={{ fontWeight: 600 }}>Form 990-EZ</span> are due
                    by the{' '}
                    <span style={{ fontWeight: 600 }}>
                      15th day of the 5th month
                    </span>{' '}
                    after the end of the organization’s fiscal year. For the
                    organizations with a calendar year-end of{' '}
                    <Hidden smDown>
                      {' '}
                      <br />
                    </Hidden>
                    <span style={{ fontWeight: 600 }}>December 31</span>, the
                    due date for the 2025 tax year is{' '}
                    <span style={{ fontWeight: 600 }}>May 15, 2026.</span>
                  </Typography>

                  {/* <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990duedates`}
                  >
                    File 8868 Now
                  </Button> */}
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common Scenarios
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  1. If my organization has gross receipts of $250,000 and
                  assets of $450,000, which form should I file?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  You must file{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990
                  </Link>{' '}
                  because your gross receipts exceed $200,000.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  2. If my organization has gross receipts of $180,000 and
                  assets of $480,000, which form should I file?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  You can choose to file{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-EZ
                  </Link>{' '}
                  where your gross receipts are{' '}
                  <span style={{ fontWeight: 600 }}> less than $200,000</span>{' '}
                  and total assets are{' '}
                  <span style={{ fontWeight: 600 }}>less than $500,000</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  3. Can an organization eligible for Form 990-EZ choose to file
                  Form 990 instead?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Yes!</span> Organizations
                  that are eligible to file{' '}
                  <span style={{ fontWeight: 600 }}>Form 990-EZ</span> may
                  choose to{' '}
                  <span style={{ fontWeight: 600 }}>file Form 990</span>. Some
                  organizations prefer to file the full Form 990 to provide more
                  detailed financial information or to meet funder or
                  transparency expectations.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                  4. Will filing Form 990 instead of Form 990-EZ significantly
                  impact how our organization is perceived by the public?
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 4 }}>
                  <span style={{ fontWeight: 600 }}> Filing Form 990</span> can
                  positively impact public perception by demonstrating a
                  commitment to transparency and detailed reporting. This can
                  enhance your organization&apos;s reputation and potentially
                  attract more support from donors and the community.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above information is given based on the IRS
                        guidelines. For more details, please refer the
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions of Form 990{' '}
                        </Link>
                        and{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990ez.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 990-EZ
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to Take the Next Step?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Ensure your nonprofit&apos;s success with the right filing choice.
              <br />
              TaxZerone is here to help you navigate{' '}
              <span style={{ fontWeight: 600 }}> Form 990</span> or{' '}
              <span style={{ fontWeight: 600 }}>Form 990-EZ</span> process with
              ease.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990vs990EZ`}
              >
                E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form990vsForm990EZ;
